// Player theming. The control bar is styled entirely from CSS custom properties
// (--havik-*), so a host can re-skin it by passing a `theme` to createPlayer OR
// by overriding the variables in their own stylesheet targeting `.havik-player`.

export interface HavikTheme {
  /** Primary accent (play button, progress fill, live dot, focus). */
  accent: string;
  /** Text/icon color that sits ON the accent (e.g. on the play button). */
  accentText: string;
  /** Letterbox / behind-video background. */
  background: string;
  /** Control bar + menu surface color. */
  surface: string;
  /** Hover/active surface color. */
  surfaceMuted: string;
  /** Primary text/icon color. */
  text: string;
  /** Secondary text color (timestamps, inactive). */
  textMuted: string;
  /** Hairline borders. */
  border: string;
  /** Corner radius for buttons/menus. */
  radius: string;
  /** Font stack for control-bar text. */
  fontFamily: string;
  /** Optional logo shown as a small corner watermark (URL). */
  logoUrl?: string;
}

/**
 * Default skin — Oddin.gg brand identity: gold accent on dark navy, white text.
 */
export const ODDIN_THEME: HavikTheme = {
  accent: '#E1B600',
  accentText: '#1B1C23',
  background: '#0e0f13',
  surface: '#1B1C23',
  surfaceMuted: '#2a2c36',
  text: '#ffffff',
  textMuted: '#9aa0ad',
  border: 'rgba(255, 255, 255, 0.1)',
  radius: '8px',
  fontFamily:
    "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const VAR: Record<keyof Omit<HavikTheme, 'logoUrl'>, string> = {
  accent: '--havik-accent',
  accentText: '--havik-accent-text',
  background: '--havik-bg',
  surface: '--havik-surface',
  surfaceMuted: '--havik-surface-muted',
  text: '--havik-text',
  textMuted: '--havik-text-muted',
  border: '--havik-border',
  radius: '--havik-radius',
  fontFamily: '--havik-font',
};

/** Apply a (possibly partial) theme to a player root element as CSS variables. */
export function applyTheme(root: HTMLElement, theme?: Partial<HavikTheme>): void {
  const merged: HavikTheme = { ...ODDIN_THEME, ...theme };
  for (const key of Object.keys(VAR) as Array<keyof typeof VAR>) {
    root.style.setProperty(VAR[key], merged[key]);
  }
  if (merged.logoUrl) root.style.setProperty('--havik-logo', `url("${merged.logoUrl}")`);
}
